import { useEffect } from 'react';

import {
  getNextWorkspaceAgentRestPollDelay,
  WORKSPACE_AGENT_REST_POLL_INITIAL_MS,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentRestPoll';

export type UsePrivateWorkspaceAgentStallMessageRefreshParams = {
  hasPendingApproval: boolean;
  isCancelling: boolean;
  isSessionRunning: boolean;
  lastMessageActivityKey: string | null;
  refreshLiveSessionMessages: () => Promise<void>;
  refreshOpenSession: () => Promise<void>;
  sessionId: string | null;
};

/**
 * While the open session is working, REST-gap-fill after a quiet stretch so a missed WSS payload
 * does not leave the thread or session status stale. Starts at 5s and doubles up to 5 minutes.
 * GET the open session once (not the full list, and not a second elicitation invalidate — that
 * query is the same session GET). The first tick is 5s later, so status should already be
 * `processing` rather than the immediate post-send `idle`. Approval and client run state stop
 * the clock. A new message resets to 5s.
 */
export const usePrivateWorkspaceAgentStallMessageRefresh = ({
  hasPendingApproval,
  isCancelling,
  isSessionRunning,
  lastMessageActivityKey,
  refreshLiveSessionMessages,
  refreshOpenSession,
  sessionId,
}: UsePrivateWorkspaceAgentStallMessageRefreshParams): void => {
  useEffect(() => {
    if (sessionId === null || !isSessionRunning || hasPendingApproval || isCancelling) {
      return;
    }

    const threadActivityKey = lastMessageActivityKey;

    let isCancelled = false;
    let timeoutId = 0;
    let delayMs = WORKSPACE_AGENT_REST_POLL_INITIAL_MS;

    const schedule = () => {
      timeoutId = window.setTimeout(() => {
        if (isCancelled) {
          return;
        }

        void Promise.all([refreshLiveSessionMessages(), refreshOpenSession()])
          .catch(() => {
            // Best-effort gap-fill — a failed message list or session GET must not surface as a chat error.
          })
          .then(() => {
            if (isCancelled || threadActivityKey !== lastMessageActivityKey) {
              return;
            }

            delayMs = getNextWorkspaceAgentRestPollDelay(delayMs);
            schedule();
          });
      }, delayMs);
    };

    schedule();

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    hasPendingApproval,
    isCancelling,
    isSessionRunning,
    lastMessageActivityKey,
    refreshLiveSessionMessages,
    refreshOpenSession,
    sessionId,
  ]);
};
